import type { PostureBucket } from "@/lib/api/posture";
import type { Severity } from "@/lib/api/types";

/**
 * The four overview buckets, grouped by criticality (D27, 2026-10-06; it
 * superseded D24's Basic / Extended groups).
 *
 * The sp-ms365 rule set absorbed the former Foundations benchmark (CIS
 * references; D22); CIS control numbers survive only as hidden reference
 * metadata on a check. The rule set is always on — it is not a value of
 * enabled_frameworks. D27 shows exactly four buckets, in this order:
 *
 *   1 Critical & high        (framework id "own_base"): the rule set's critical and high checks.
 *   2 Medium & low           (framework id "own_lower"): its medium, low and informational checks.
 *   3 AI best practice       (framework id "ai_bp"): module-owned (D20); "Switched off" when off.
 *   4 Microsoft Secure Score (framework id "secure_score"): Microsoft's own number, a cross-check.
 *
 * Buckets 1 and 2 are disjoint, and together they are every check of the rule
 * set. There is no overall score: the all-checks score (framework id "own")
 * is still computed for machine consumers but no surface shows it. The
 * Microsoft baseline is not a bucket — it lives on Posture → Microsoft baseline
 * and in the report's baseline controls section. A bucket is a heading, never a
 * number: each score carries its own coverage over its own universe and no two
 * scores are ever blended or averaged (invariant 1).
 */
export const RULE_SET_CRITICAL_HIGH = "own_base";
export const RULE_SET_MEDIUM_LOW = "own_lower";
/** The all-checks score. Computed, never shown since D27. */
export const RULE_SET_ALL = "own";

export const CRITICAL_HIGH_LABEL = "Critical & high";
export const MEDIUM_LOW_LABEL = "Medium & low";
export const AI_BP_LABEL = "AI best practice";
export const SECURE_SCORE_LABEL = "Microsoft Secure Score";
export const BASELINE_LABEL = "Microsoft baseline";
/** A plain name for the all-checks score, only so a stray one never shows its id. */
export const RULE_SET_ALL_LABEL = "sp-ms365 rule set, all checks";

/** The source line under the two rule-set buckets. */
export const RULE_SET_NAME = "sp-ms365 rule set";

/** The German labels the printed report uses; the console itself is English. */
export const BUCKET_LABELS_DE = {
  [RULE_SET_CRITICAL_HIGH]: "Kritisch & hoch",
  [RULE_SET_MEDIUM_LOW]: "Mittel & niedrig",
  ai_bp: "KI-Best-Practice",
  secure_score: "Microsoft Secure Score",
} as const;

/** One of the two rule-set buckets, as the overview and the Posture tabs show it. */
export interface RuleSetBucket {
  /** The score's framework id in the API's `scores`. */
  framework: typeof RULE_SET_CRITICAL_HIGH | typeof RULE_SET_MEDIUM_LOW;
  /** ?bucket= on /posture and /posture/state-counts (checks.Bucket). */
  bucket: PostureBucket;
  label: string;
  /** The Posture tab's path under the tenant. */
  path: string;
  /** The severities in the bucket (checks.InBase / checks.InLower). */
  severities: readonly Severity[];
  /** One line under the bucket's heading. */
  blurb: string;
  /** The line under its score. */
  caption: string;
  testid: string;
}

export const CRITICAL_HIGH_BUCKET: RuleSetBucket = {
  framework: RULE_SET_CRITICAL_HIGH,
  bucket: "critical_high",
  label: CRITICAL_HIGH_LABEL,
  path: "/posture/critical-high",
  severities: ["critical", "high"],
  blurb: "sp-ms365 rule set: its critical and high-severity checks.",
  caption:
    "Pass vs all: the severity-weighted share of every applicable critical and high check that passed. Pass vs executed counts only the checks that produced a verdict. Coverage is over these checks alone.",
  testid: "critical-high",
};

export const MEDIUM_LOW_BUCKET: RuleSetBucket = {
  framework: RULE_SET_MEDIUM_LOW,
  bucket: "medium_low",
  label: MEDIUM_LOW_LABEL,
  path: "/posture/medium-low",
  severities: ["medium", "low", "informational"],
  blurb:
    "sp-ms365 rule set: its medium, low and informational checks (informational is included here).",
  caption:
    "Pass vs all: the severity-weighted share of every applicable medium, low and informational check that passed. Pass vs executed counts only the checks that produced a verdict. Coverage is over these checks alone.",
  testid: "medium-low",
};
